import { useState, useEffect, useCallback, useRef } from "react";
import { supabase } from "../config/supabase";
import { useAuth } from "../context/AuthContext";
import { criarFases } from "../data/fases";
import { criarFasesCipamin } from "../data/fasesCipamin";
import { criarFasesCipatr } from "../data/fasesCipatr";
import { Fase } from "../types";
import {
  Users,
  FileText,
  Vote,
  CheckCircle,
  Award,
  Calendar,
} from "lucide-react";
import { getTipoComissao } from "../utils/cipaminUtils";
import {
  arquivarCronogramaAnterior,
  confirmarAcaoCronograma,
  retificarAcaoCronograma,
  podeEditarAcao,
} from "../services/cronogramaHistoricoService";

// Função para obter o nome do ícone a partir do componente
const getIconName = (iconComponent: React.ComponentType<any>): string => {
  if (iconComponent === Users) return "Users";
  if (iconComponent === FileText) return "FileText";
  if (iconComponent === Vote) return "Vote";
  if (iconComponent === CheckCircle) return "CheckCircle";
  if (iconComponent === Award) return "Award";
  if (iconComponent === Calendar) return "Calendar";
  return "Calendar"; // fallback
};

interface AcaoCronograma {
  id: string;
  fase_id: string;
  usuario_id: string;
  empresa_id?: string;
  descricao: string;
  obrigatoria: boolean;
  concluida: boolean;
  data_conclusao?: string;
  ordem: number;
  // Campos de confirmação e versionamento (FASE 2 - NR-05 compliance)
  confirmed_at?: string | null;
  confirmed_by?: string | null;
  confirmed_by_nome?: string | null;
  confirmed_by_email?: string | null;
  versao?: number;
  original_id?: string | null;
  retificada?: boolean;
  retificada_em?: string | null;
  motivo_retificacao?: string | null;
  observacao?: string | null;
}

interface FaseComAcoes extends Fase {
  acoes_banco: AcaoCronograma[];
}

export const useCronograma = () => {
  const { user, empresaSelecionada } = useAuth();
  const [fases, setFases] = useState<FaseComAcoes[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dataTerminoMandato, setDataTerminoMandato] = useState<string>("");
  const lastLoadRef = useRef<string>("");

  // Criar fases padrão para novos usuários
  const criarFasesPadrao = useCallback(
    async (novaDataTermino?: string) => {
      if (!user) return;

      try {
        console.log(
          "🔄 [useCronograma] Criando fases padrão para o usuário...",
        );
        console.log("📊 [useCronograma] Dados do usuário:", {
          id: user.id,
          nome: user.nome_completo,
        });
        console.log(
          "📊 [useCronograma] Empresa selecionada:",
          empresaSelecionada?.nome,
          "ID:",
          empresaSelecionada?.id,
        );
        console.log("📊 [useCronograma] Empresa completa:", empresaSelecionada);
        console.log(
          "📊 [useCronograma] tipo_comissao do banco:",
          (empresaSelecionada as any)?.tipo_comissao,
        );
        console.log(
          "📊 [useCronograma] cnae_principal:",
          (empresaSelecionada as any)?.cnae_principal,
        );

        // Detectar tipo de comissão (CIPA ou CIPAMIN)
        const tipoComissaoBanco = (empresaSelecionada as any)?.tipo_comissao;
        const cnaeEmpresa = (empresaSelecionada as any)?.cnae_principal;
        const nomeEmpresa = (empresaSelecionada as any)?.nome;
        const tipoComissaoCalculado = getTipoComissao(cnaeEmpresa, nomeEmpresa);
        const tipoComissao = tipoComissaoBanco || tipoComissaoCalculado;

        console.log(
          `🏗️ [useCronograma] Tipo de comissão detectado: ${tipoComissao}`,
        );
        console.log(
          `🏗️ [useCronograma] - Fonte: ${tipoComissaoBanco ? "banco de dados" : cnaeEmpresa ? "calculado pelo CNAE" : "calculado pelo nome da empresa"}`,
        );

        // FASE 2 - ARQUIVAR ANTES DE DELETAR (NR-05 compliance)
        // Buscar mandato ativo para registrar histórico E obter data de término
        let dataTerminoDoMandatoAtivo: string | null = null;

        if (empresaSelecionada?.id) {
          const { data: mandatoAtivo } = await supabase
            .from("cp_cipa_mandato")
            .select("id, data_fim")
            .eq("empresa_id", empresaSelecionada.id)
            .eq("status", "ativo")
            .single();

          if (mandatoAtivo) {
            // Guardar a data de término do mandato ativo para usar depois
            dataTerminoDoMandatoAtivo = mandatoAtivo.data_fim;
            console.log(
              "📅 [useCronograma] Data de término do mandato ativo:",
              dataTerminoDoMandatoAtivo,
            );

            console.log(
              "📦 [useCronograma] Arquivando cronograma anterior antes de recriar...",
            );
            await arquivarCronogramaAnterior(
              empresaSelecionada.id,
              mandatoAtivo.id,
              user.id,
              novaDataTermino
                ? `Recriação com nova data de término: ${novaDataTermino}`
                : "Criação inicial do cronograma",
            );
          }
        }

        // Deletar apenas ações NÃO confirmadas e NÃO concluídas
        // Ações confirmadas ou concluídas ficam arquivadas no histórico
        let deleteFasesQuery = supabase
          .from("cp_fases")
          .delete()
          .eq("usuario_id", user.id)
          .is("confirmed_at", null); // Só fases não confirmadas

        let deleteAcoesQuery = supabase
          .from("cp_acoes_cronograma")
          .delete()
          .eq("usuario_id", user.id)
          .is("confirmed_at", null) // Só ações não confirmadas
          .eq("concluida", false); // E não concluídas

        // Aplicar filtro de empresa se necessário
        if (empresaSelecionada && user.tipo_usuario !== "SUPER_ADMIN") {
          console.log(
            "🗑️ Deletando fases da empresa:",
            empresaSelecionada.nome,
          );
          deleteFasesQuery = deleteFasesQuery.eq(
            "empresa_id",
            empresaSelecionada.id,
          );
          deleteAcoesQuery = deleteAcoesQuery.eq(
            "empresa_id",
            empresaSelecionada.id,
          );
        } else {
          console.log("🗑️ Deletando todas as fases do usuário");
        }

        await deleteFasesQuery;
        await deleteAcoesQuery;

        // PRIORIDADE: 1) nova data fornecida, 2) mandato ativo, 3) empresa, 4) usuário, 5) fallback
        const dataTermino =
          novaDataTermino ||
          dataTerminoDoMandatoAtivo ||
          empresaSelecionada?.data_termino_mandato ||
          user.data_termino_mandato ||
          "2025-09-26";

        // Identificar fonte da data para debug
        let fonteData = "fallback padrão";
        if (novaDataTermino) fonteData = "nova data fornecida";
        else if (dataTerminoDoMandatoAtivo)
          fonteData = "mandato ativo (cp_cipa_mandato)";
        else if (empresaSelecionada?.data_termino_mandato)
          fonteData = "empresa (legado)";
        else if (user.data_termino_mandato) fonteData = "usuário (legado)";

        console.log("📅 Data de término do mandato:", dataTermino);
        console.log("📅 Fonte da data:", fonteData);

        // Definir a data de término no estado
        setDataTerminoMandato(dataTermino);

        // Criar fases baseado no tipo de comissão
        let fasesDefault: Fase[];
        if (tipoComissao === "CIPAMIN") {
          fasesDefault = criarFasesCipamin(dataTermino);
        } else if (tipoComissao === "CIPATR") {
          fasesDefault = criarFasesCipatr(dataTermino);
        } else {
          fasesDefault = criarFases(dataTermino);
        }

        console.log(`📋 Fases ${tipoComissao} criadas:`, fasesDefault.length);

        // Preparar dados para batch insert
        const fasesParaInserir = fasesDefault.map((fase, i) => {
          console.log(
            `📝 Fase ${i + 1}: ${fase.titulo} - ${fase.data.toISOString().split("T")[0]}`,
          );
          return {
            usuario_id: user.id,
            empresa_id: empresaSelecionada?.id || null,
            fase_tipo: fase.id,
            titulo: fase.titulo,
            data_planejada: fase.data,
            data_fim: fase.dataFim,
            artigo_nr: fase.artigo,
            prazo_nr: fase.prazoNR,
            cor: fase.cor,
            icon: getIconName(fase.icon),
            ordem: i + 1,
            tipo_comissao: tipoComissao,
          };
        });

        console.log("💾 Inserindo fases no banco de dados...");
        // Inserir todas as fases de uma vez
        const { data: fasesInseridas, error: fasesError } = await supabase
          .from("cp_fases")
          .insert(fasesParaInserir)
          .select();

        if (fasesError) {
          console.error("❌ Erro ao criar fases:", fasesError);
          throw fasesError;
        }
        console.log("✅ Fases inseridas com sucesso:", fasesInseridas?.length);

        // Preparar e inserir todas as ações de uma vez
        const acoesParaInserir: any[] = [];

        fasesInseridas?.forEach((faseInserida, index) => {
          const fase = fasesDefault[index];
          if (fase.acoes && fase.acoes.length > 0) {
            console.log(`📝 Ações da fase ${fase.titulo}:`, fase.acoes.length);
            const acoes = fase.acoes.map((acao, acaoIndex) => ({
              fase_id: faseInserida.id,
              usuario_id: user.id,
              empresa_id: empresaSelecionada?.id || null,
              descricao: acao,
              obrigatoria: true,
              ordem: acaoIndex + 1,
              tipo_comissao: tipoComissao,
            }));
            acoesParaInserir.push(...acoes);
          }
        });

        if (acoesParaInserir.length > 0) {
          console.log(
            "💾 Inserindo ações no banco de dados...",
            acoesParaInserir.length,
            "ações",
          );
          const { error: acoesError } = await supabase
            .from("cp_acoes_cronograma")
            .insert(acoesParaInserir);

          if (acoesError) {
            console.error("❌ Erro ao criar ações:", acoesError);
            throw acoesError;
          }
          console.log("✅ Ações inseridas com sucesso");
        }

        console.log("✅ Fases padrão criadas com sucesso!");
      } catch (err) {
        console.error("❌ Erro ao criar fases padrão:", err);
        setError(
          err instanceof Error ? err.message : "Erro ao criar fases padrão",
        );
      }
    },
    [user, empresaSelecionada],
  );

  // Carregar fases do banco de dados
  const carregarFases = useCallback(async () => {
    if (!user) return;

    try {
      setLoading(true);
      console.log("🔄 Carregando fases do banco de dados...");
      console.log("📊 Usuário:", user.id, user.nome_completo);
      console.log(
        "📊 Empresa selecionada:",
        empresaSelecionada?.nome,
        empresaSelecionada?.id,
      );

      // Buscar fases do usuário
      let query = supabase
        .from("cp_fases")
        .select("*")
        .eq("usuario_id", user.id);

      // Aplicar filtro de empresa se necessário
      if (empresaSelecionada && user.tipo_usuario !== "SUPER_ADMIN") {
        console.log("🔍 Filtrando por empresa:", empresaSelecionada.nome);
        // Buscar fases da empresa específica
        query = query.eq("empresa_id", empresaSelecionada.id);
      } else {
        console.log("🔍 Carregando todas as fases do usuário");
      }
      const { data: fasesBanco, error: fasesError } =
        await query.order("ordem");

      if (fasesError) {
        console.error("❌ Erro ao carregar fases:", fasesError);
        throw fasesError;
      }

      /*
      console.log('📊 Fases encontradas no banco:', fasesBanco?.length || 0);
      if (fasesBanco && fasesBanco.length > 0) {
        fasesBanco.forEach((fase, index) => {
          console.log(`📅 Fase ${index + 1}: ${fase.titulo} - ${fase.data_planejada}`);
        });
      }
        */

      // Buscar ações das fases
      let acoesQuery = supabase
        .from("cp_acoes_cronograma")
        .select("*")
        .eq("usuario_id", user.id);

      // Aplicar filtro de empresa se necessário
      if (empresaSelecionada && user.tipo_usuario !== "SUPER_ADMIN") {
        console.log("🔍 Filtrando ações por empresa:", empresaSelecionada.nome);
        // Buscar ações da empresa específica
        acoesQuery = acoesQuery.eq("empresa_id", empresaSelecionada.id);
      } else {
        console.log("🔍 Carregando todas as ações do usuário");
      }

      const { data: acoesBanco, error: acoesError } =
        await acoesQuery.order("ordem");

      if (acoesError) {
        console.error("❌ Erro ao carregar ações:", acoesError);
        throw acoesError;
      }

      console.log("📊 Ações encontradas no banco:", acoesBanco?.length || 0);

      // Se não há fases no banco, criar as fases padrão
      if (!fasesBanco || fasesBanco.length === 0) {
        console.log("📝 Criando fases padrão...");
        await criarFasesPadrao();
        return;
      }

      // Buscar data de término do mandato ativo (prioridade)
      let dataTerminoDoMandatoAtivo: string | null = null;
      if (empresaSelecionada?.id) {
        const { data: mandatoAtivo } = await supabase
          .from("cp_cipa_mandato")
          .select("data_fim")
          .eq("empresa_id", empresaSelecionada.id)
          .eq("status", "ativo")
          .single();

        if (mandatoAtivo?.data_fim) {
          dataTerminoDoMandatoAtivo = mandatoAtivo.data_fim;
        }
      }

      // PRIORIDADE: 1) mandato ativo, 2) empresa (legado), 3) usuário (legado), 4) fallback
      const dataTermino =
        dataTerminoDoMandatoAtivo ||
        empresaSelecionada?.data_termino_mandato ||
        user.data_termino_mandato ||
        "2025-09-26";
      setDataTerminoMandato(dataTermino);

      // Se o mandato ativo mudou, recriar cronograma para evitar datas antigas
      if (dataTerminoDoMandatoAtivo && fasesBanco.length > 0) {
        const tipoComissaoBanco = (empresaSelecionada as any)?.tipo_comissao;
        const cnaeEmpresa = (empresaSelecionada as any)?.cnae_principal;
        const nomeEmpresa = (empresaSelecionada as any)?.nome;
        const tipoComissaoCalculado = getTipoComissao(cnaeEmpresa, nomeEmpresa);
        const tipoComissao = tipoComissaoBanco || tipoComissaoCalculado;

        const fasesEsperadas =
          tipoComissao === "CIPAMIN"
            ? criarFasesCipamin(dataTerminoDoMandatoAtivo)
            : tipoComissao === "CIPATR"
              ? criarFasesCipatr(dataTerminoDoMandatoAtivo)
              : criarFases(dataTerminoDoMandatoAtivo);

        const primeiraFaseEsperada = fasesEsperadas[0]?.data;
        const primeiraFaseAtual = fasesBanco[0]?.data_planejada
          ? new Date(fasesBanco[0].data_planejada)
          : null;

        const diferencaDias =
          primeiraFaseEsperada && primeiraFaseAtual
            ? Math.abs(
                Math.round(
                  (primeiraFaseEsperada.getTime() -
                    primeiraFaseAtual.getTime()) /
                    (1000 * 60 * 60 * 24),
                ),
              )
            : 0;

        if (primeiraFaseEsperada && primeiraFaseAtual && diferencaDias >= 1) {
          console.log(
            "⚠️ [useCronograma] Cronograma desatualizado em relação ao mandato ativo. Recriando...",
            {
              dataMandatoAtivo: dataTerminoDoMandatoAtivo,
              primeiraFaseEsperada: primeiraFaseEsperada.toISOString(),
              primeiraFaseAtual: primeiraFaseAtual.toISOString(),
              diferencaDias,
            },
          );

          await recriarCronograma(dataTerminoDoMandatoAtivo);
          return;
        }
      }

      // Combinar fases com suas ações
      const fasesComAcoes: FaseComAcoes[] = fasesBanco.map((fase) => {
        const acoesDesaFase =
          acoesBanco?.filter((acao) => acao.fase_id === fase.id) || [];

        return {
          id: fase.id,
          titulo: fase.titulo,
          data: new Date(fase.data_planejada),
          dataFim: fase.data_fim ? new Date(fase.data_fim) : undefined,
          artigo: fase.artigo_nr || "",
          prazoNR: fase.prazo_nr || "",
          cor: fase.cor || "bg-blue-500",
          icon: typeof fase.icon === "string" ? fase.icon : "Calendar",
          acoes: acoesDesaFase.map((acao) => acao.descricao), // Para compatibilidade
          acoes_banco: acoesDesaFase,
          concluida: fase.concluida || false,
          observacoes: fase.observacoes,
        };
      });

      console.log("✅ Dados carregados:", {
        fasesBanco: fasesBanco.length,
        acoesBanco: acoesBanco?.length || 0,
      });
      setFases(fasesComAcoes);
    } catch (err) {
      console.error("❌ Erro ao carregar cronograma:", err);
      setError(err instanceof Error ? err.message : "Erro desconhecido");
    } finally {
      setLoading(false);
    }
  }, [user, empresaSelecionada]);

  // Marcar/desmarcar ação como concluída
  const toggleAcao = async (faseId: string, acaoIndex: number) => {
    if (!user) return;

    try {
      const fase = fases.find((f) => f.id === faseId);
      if (!fase || !fase.acoes_banco[acaoIndex]) return;

      const acao = fase.acoes_banco[acaoIndex];

      // FASE 2 - Verificar se a ação pode ser editada (NR-05 compliance)
      const verificacao = podeEditarAcao(acao);
      if (!verificacao.permitido) {
        console.warn(
          "⚠️ [useCronograma] Ação não pode ser editada:",
          verificacao.motivo,
        );
        setError(verificacao.motivo || "Ação não pode ser editada");
        return;
      }

      const novoConcluida = !acao.concluida;

      let updateQuery = supabase
        .from("cp_acoes_cronograma")
        .update({
          concluida: novoConcluida,
          data_conclusao: novoConcluida ? new Date().toISOString() : null,
          updated_at: new Date().toISOString(),
        })
        .eq("id", acao.id);

      // Aplicar filtro de empresa se necessário
      if (empresaSelecionada && user.tipo_usuario !== "SUPER_ADMIN") {
        updateQuery = updateQuery.eq("empresa_id", empresaSelecionada.id);
      }

      const { error } = await updateQuery;

      if (error) {
        console.error("❌ Erro ao atualizar ação:", error);
        throw error;
      }

      // Atualizar estado local
      setFases((prev) => {
        const novasFases = prev.map((f) => {
          if (f.id === faseId) {
            const novasAcoes = [...f.acoes_banco];
            novasAcoes[acaoIndex] = {
              ...novasAcoes[acaoIndex],
              concluida: novoConcluida,
              data_conclusao: novoConcluida
                ? new Date().toISOString()
                : undefined,
            };
            return { ...f, acoes_banco: novasAcoes };
          }
          return f;
        });

        return novasFases;
      });
    } catch (err) {
      console.error("❌ Erro ao toggle ação:", err);
      setError(err instanceof Error ? err.message : "Erro ao atualizar ação");
    }
  };

  /**
   * FASE 2 - Confirmar ação do cronograma (torna imutável)
   * Após confirmação, a ação não pode mais ser editada - apenas retificada.
   */
  const confirmarAcao = async (
    faseId: string,
    acaoIndex: number,
  ): Promise<boolean> => {
    if (!user) return false;

    try {
      const fase = fases.find((f) => f.id === faseId);
      if (!fase || !fase.acoes_banco[acaoIndex]) {
        setError("Ação não encontrada");
        return false;
      }

      const acao = fase.acoes_banco[acaoIndex];

      // Verificar se já está confirmada
      if (acao.confirmed_at) {
        setError("Esta ação já foi confirmada");
        return false;
      }

      // Verificar se está concluída (só pode confirmar ações concluídas)
      if (!acao.concluida) {
        setError("Apenas ações concluídas podem ser confirmadas");
        return false;
      }

      console.log("🔒 [useCronograma] Confirmando ação:", acao.descricao);

      await confirmarAcaoCronograma(
        acao.id,
        user.id,
        user.nome_completo || user.email || "Usuário",
        user.email || "",
      );

      // Atualizar estado local
      setFases((prev) => {
        return prev.map((f) => {
          if (f.id === faseId) {
            const novasAcoes = [...f.acoes_banco];
            novasAcoes[acaoIndex] = {
              ...novasAcoes[acaoIndex],
              confirmed_at: new Date().toISOString(),
              confirmed_by: user.id,
              confirmed_by_nome: user.nome_completo || user.email || "Usuário",
              confirmed_by_email: user.email || "",
            };
            return { ...f, acoes_banco: novasAcoes };
          }
          return f;
        });
      });

      console.log("✅ [useCronograma] Ação confirmada com sucesso");
      return true;
    } catch (err) {
      console.error("❌ Erro ao confirmar ação:", err);
      setError(err instanceof Error ? err.message : "Erro ao confirmar ação");
      return false;
    }
  };

  /**
   * FASE 2 - Retificar ação do cronograma (cria nova versão, preserva original)
   * Usado para corrigir ações já confirmadas sem perder histórico.
   */
  const retificarAcao = async (
    faseId: string,
    acaoIndex: number,
    novosDados: { descricao?: string; observacao?: string },
    motivo: string,
  ): Promise<boolean> => {
    if (!user) return false;

    try {
      const fase = fases.find((f) => f.id === faseId);
      if (!fase || !fase.acoes_banco[acaoIndex]) {
        setError("Ação não encontrada");
        return false;
      }

      const acao = fase.acoes_banco[acaoIndex];

      console.log("📝 [useCronograma] Retificando ação:", acao.descricao);

      const novaAcaoId = await retificarAcaoCronograma(
        acao.id,
        novosDados,
        motivo,
        user.id,
      );

      // Recarregar fases para obter estado atualizado
      await carregarFases();

      console.log(
        "✅ [useCronograma] Ação retificada com sucesso. Nova versão:",
        novaAcaoId,
      );
      return true;
    } catch (err) {
      console.error("❌ Erro ao retificar ação:", err);
      setError(err instanceof Error ? err.message : "Erro ao retificar ação");
      return false;
    }
  };

  /**
   * Verificar se uma ação pode ser editada
   */
  const verificarPodeEditarAcao = (
    faseId: string,
    acaoIndex: number,
  ): { permitido: boolean; motivo?: string } => {
    const fase = fases.find((f) => f.id === faseId);
    if (!fase || !fase.acoes_banco[acaoIndex]) {
      return { permitido: false, motivo: "Ação não encontrada" };
    }
    return podeEditarAcao(fase.acoes_banco[acaoIndex]);
  };

  // Calcular estatísticas do cronograma
  const getEstatisticas = useCallback(() => {
    const agora = new Date();
    let vencidas = 0;
    let proximoVencimento = 0;
    let concluidas = 0;
    const fasesVencidas: FaseComAcoes[] = [];

    fases.forEach((fase) => {
      const dataFase = new Date(fase.data);
      const diasRestantes = Math.ceil(
        (dataFase.getTime() - agora.getTime()) / (1000 * 60 * 60 * 24),
      );

      // Verificar se fase está vencida
      if (diasRestantes < 0) {
        vencidas++;
        fasesVencidas.push(fase);
      } else if (diasRestantes <= 7) {
        proximoVencimento++;
      }

      // Verificar se fase está concluída (todas as ações marcadas)
      const acoesTotais = fase.acoes_banco.length;
      const acoesConcluidas = fase.acoes_banco.filter(
        (acao) => acao.concluida,
      ).length;

      if (acoesTotais > 0 && acoesConcluidas === acoesTotais) {
        concluidas++;
      }
    });

    return {
      vencidas,
      proximoVencimento,
      concluidas,
      total: fases.length,
      fasesVencidas,
    };
  }, [fases]);

  // Recriar cronograma com nova data de término
  const recriarCronograma = useCallback(
    async (novaDataTermino?: string) => {
      if (!user) return;

      try {
        setLoading(true);
        console.log("🔄 Recriando cronograma com nova data...");

        // Criar novas fases com a data atualizada
        await criarFasesPadrao(novaDataTermino);

        // Recarregar dados
        await carregarFases();
      } catch (err) {
        console.error("❌ Erro ao recriar cronograma:", err);
        setError(
          err instanceof Error ? err.message : "Erro ao recriar cronograma",
        );
      } finally {
        setLoading(false);
      }
    },
    [user, empresaSelecionada],
  );

  // Carregar dados quando o usuário ou empresa muda
  useEffect(() => {
    if (user) {
      const currentKey = `${user.id}-${empresaSelecionada?.id || "null"}`;
      console.log(
        "🔄 useEffect carregarFases - user:",
        user.id,
        "empresa:",
        empresaSelecionada?.id,
      );

      // Só carregar se a chave mudou
      if (lastLoadRef.current !== currentKey) {
        lastLoadRef.current = currentKey;
        carregarFases();
      } else {
        console.log("⏭️ Pulando carregamento - mesma chave:", currentKey);
      }
    }
  }, [user, empresaSelecionada, carregarFases]);

  return {
    fases,
    loading,
    error,
    dataTerminoMandato,
    toggleAcao,
    carregarFases,
    recriarCronograma,
    getEstatisticas,
    // FASE 2 - Funções de confirmação e retificação (NR-05 compliance)
    confirmarAcao,
    retificarAcao,
    verificarPodeEditarAcao,
  };
};
